<template>
  <div class="flex flex-col min-h-screen bg-gray-100">
    <header class="flex flex-col w-full border-b">
      <Navbar :activeTab="0" />
    </header>
    <div
      v-if="isLoading"
      class="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
    >
      <!-- Spinner -->
      <div
        class="animate-spin rounded-full h-32 w-32 border-t-4 border-b-4 border-primary-purple"
      ></div>
    </div>
    <div
      v-if="errorMessage"
      role="alert"
      class="flex gap-2 w-full justify-center p-2.5 mt-6 text-sm leading-5 text-center text-red-900 bg-red-50 rounded-lg max-md:flex-wrap"
      style="max-height: 80px; overflow-y: auto"
    >
      <div
        class="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full"
      >
        <img
          src="../../assets/shared/failure.svg"
          alt="Error Icon"
          class="w-4 h-4"
        />
      </div>
      <p>{{ errorMessage }}</p>
    </div>
    <div class="flex flex-col gap-6 mb-16 p-6">
      <!-- page title -->
      <div class="flex flex-row items-center gap-2">
        <span class="text-black text-3xl font-semibold leading-9 text-left"
          >Subscription</span
        >
        <span
          v-if="currentSubscriptionStatus"
          class="rounded-md px-2 py-1 text-sm font-medium leading-5 text-left"
          :class="
            currentSubscriptionStatus === 'active'
              ? 'bg-green-200 text-green-900'
              : 'bg-red-200 text-red-900'
          "
          >{{ currentSubscriptionStatus?.toUpperCase() }}</span
        >
      </div>
      <!-- billing cycle toggle -->
      <div class="flex flex-col">
        <div class="flex flex-col">
          <div class="flex flex-row max-w-max gap-2 bg-gray-200 rounded-lg p-1">
            <BaseButton
              @click="showMonthlyPlans()"
              class="px-4 py-1 text-black text-sm font-medium leading-5"
              :class="
                isMonthlyPlansSelected
                  ? 'bg-primary-purple text-white rounded-md'
                  : 'bg-gray-200'
              "
            >
              Monthly Subscription
            </BaseButton>
            <BaseButton
              @click="showYearlyPlans()"
              class="px-4 py-1 text-gray-900 text-sm font-medium leading-5"
              :class="
                isYearlyPlansSelected
                  ? 'bg-primary-purple text-white rounded-md'
                  : 'bg-gray-200'
              "
            >
              Yearly Subscription
            </BaseButton>
          </div>
        </div>
      </div>

      <div class="flex flex-col">
        <div class="flex flex-row gap-2">
          <!-- Monthly Plans -->
          <div
            v-if="isMonthlyPlansSelected"
            class="grid grid-cols-1 md:grid-cols-3 gap-6 w-full"
          >
            <SubscriptionPlanCard
              v-for="(plan, index) in monthlyPlansList"
              :key="index"
              :plan="plan"
              :is-selected="
                selectedPlan?.subscriptionPlanTierId ===
                plan.subscriptionPlanTierId
              "
              :current-amount="getCurrentMonthlyAmount()"
              :customAmount="currentSubscriptionAmount"
              @select="reviewPaymentMethod"
              @contactSupport="handleContactSupport"
              @viewPaymentMethod="reviewPaymentMethod"
            />
          </div>

          <!-- Yearly Plans -->
          <div
            v-if="isYearlyPlansSelected"
            class="grid grid-cols-1 md:grid-cols-3 gap-6 w-full"
          >
            <SubscriptionPlanCard
              v-for="(plan, index) in yearlyPlansList"
              :key="index"
              :plan="plan"
              :is-selected="
                selectedPlan?.subscriptionPlanTierId ===
                plan.subscriptionPlanTierId
              "
              :current-amount="getCurrentMonthlyAmount()"
              :customAmount="currentSubscriptionAmount"
              @select="reviewPaymentMethod"
              @contactSupport="handleContactSupport"
              @viewPaymentMethod="reviewPaymentMethod"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
  <SubscriptionDetailsModal
    :isVisible="isPaymentMethodModalVisible"
    :source="paymentMethodSource"
    :cardNumber="paymentMethodCardNumber"
    :cardExpiry="paymentMethodCardExpiry"
    :invoiceRecipientEmail="paymentMethodInvoiceRecipientEmail"
    :showBillingButton="true"
    :upcomingInvoice="mappedUpcomingInvoice"
    :isLoading="isModalLoading"
    @proceed="proceedWithPaymentMethod"
    @update:isVisible="isPaymentMethodModalVisible = false"
  />
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref, nextTick } from "vue";
import Navbar from "@/components/dashboard/Navbar.vue";
import BaseButton from "@/components/shared/BaseButton.vue";
import SubscriptionPlanCard from "@/components/subscription/SubscriptionPlanCard.vue";
import { gql } from "@apollo/client/core";
import { useMutation, useQuery } from "@vue/apollo-composable";
import { scrollToTop } from "@/utils/commonUtils";
import SubscriptionDetailsModal from "@/components/billing/subscription/SubscriptionDetailsModal.vue";
import {
  PaymentMethod,
  SubscriptionPlan,
  SubscriptionPlanInfo,
  SubscriptionPlanTier,
} from "@/types/subscription-types";

const LIST_SUBSCRIPTION_PLANS = gql`
  query ListSubscriptionPlans(
    $currentSubscription: Boolean
    $subscriptionPlanTierId: ID
  ) {
    listSubscriptionPlans(currentSubscription: $currentSubscription) {
      currentSubscription {
        status
        subscriptionPlanTierId
        amount
        upcomingInvoice(subscriptionPlanTierId: $subscriptionPlanTierId)
        autoRenewal
        billingCycle
        couponId
        couponType
        couponValue
      }
      activePaymentMethod {
        paymentMethodId
        source
        invoiceRecipientEmail
        paymentInfo {
          cardNumber
          expirationDate
        }
      }
      allPlans {
        name
        features
        payoutFeePercentage
        subscriptionPlanTiers {
          id
          amount
          billingCycle
        }
      }
    }
  }
`;

const UPSERT_SUBSCRIPTION_MUTATION = gql`
  mutation UpsertSubscription($input: UpsertSubscriptionInput!) {
    upsertSubscription(input: $input) {
      success
      errors
    }
  }
`;

export default defineComponent({
  name: "SubscriptionView",
  components: {
    Navbar,
    BaseButton,
    SubscriptionPlanCard,
    SubscriptionDetailsModal,
  },
  setup() {
    const currentSubscription = ref<SubscriptionPlanInfo | null>(null);
    const isMonthlyPlansSelected = ref(true);
    const isYearlyPlansSelected = ref(false);
    const isFetchingSubscriptionPlans = ref(false);
    const currentSubscriptionStatus = ref<string | null>(null);
    const currentActiveSubscriptionPlanTierId = ref<string | null>("");
    const currentSubscriptionAmount = ref<number | null>(null);
    const errorMessage = ref("");
    const selectedPlan = ref<SubscriptionPlanInfo | null>(null);
    const isPaymentMethodModalVisible = ref(false);
    const paymentMethodSource = ref<string>("");
    const paymentMethodCardNumber = ref<string | null>(null);
    const paymentMethodCardExpiry = ref<string | null>(null);
    const paymentMethodInvoiceRecipientEmail = ref<string | null>(null);
    const proceedWithSubscriptionPlanTierId = ref<string>("");

    const monthlyPlansList = ref<SubscriptionPlanInfo[]>([]);
    const yearlyPlansList = ref<SubscriptionPlanInfo[]>([]);
    const mappedUpcomingInvoice = ref<any>(undefined);
    const isModalLoading = ref(false);
    const planToReview = ref<string | null>(null);

    const {
      mutate: upsertSubscription,
      loading: upsertSubscriptionLoading,
      onDone: upsertSubscriptionDone,
      onError: upsertSubscriptionError,
    } = useMutation(UPSERT_SUBSCRIPTION_MUTATION);

    const { onResult: onInvoiceResult, onError: onInvoiceError } = useQuery(
      LIST_SUBSCRIPTION_PLANS,
      () => ({
        currentSubscription: true,
        subscriptionPlanTierId: planToReview.value,
      }),
      {
        enabled: computed(() => !!planToReview.value),
        fetchPolicy: "network-only",
      }
    );

    onInvoiceResult(
      (result: {
        data?: {
          listSubscriptionPlans?: {
            currentSubscription?: { upcomingInvoice?: any };
          };
        };
      }) => {
        const apiInvoice =
          result?.data?.listSubscriptionPlans?.currentSubscription
            ?.upcomingInvoice;
        if (apiInvoice) {
          mappedUpcomingInvoice.value = {
            groupedLineItems: apiInvoice.grouped_line_items,
            subtotal: apiInvoice.subtotal,
            discounts: apiInvoice.discount
              ? [{ label: apiInvoice.coupon_name, amount: apiInvoice.discount }]
              : [],
            totalExcludingTax: apiInvoice.subtotal - (apiInvoice.discount || 0),
            tax: apiInvoice.tax,
            total: apiInvoice.total,
            amountDue: apiInvoice.amount_due,
            appliedBalance: apiInvoice.applied_balance,
          };
        } else {
          mappedUpcomingInvoice.value = undefined;
        }
        isModalLoading.value = false;
      }
    );

    onInvoiceError(() => {
      mappedUpcomingInvoice.value = undefined;
      isModalLoading.value = false;
    });

    onMounted(() => {
      fetchSubscriptionPlans();
    });

    const showMonthlyPlans = () => {
      checkErrorMessage();
      isMonthlyPlansSelected.value = true;
      isYearlyPlansSelected.value = false;
    };

    const showYearlyPlans = () => {
      checkErrorMessage();
      isMonthlyPlansSelected.value = false;
      isYearlyPlansSelected.value = true;
    };

    const fetchSubscriptionPlans = () => {
      isFetchingSubscriptionPlans.value = true;
      const {
        onResult: onQuerySubscriptionPlansResult,
        onError: onQuerySubscriptionPlansError,
      } = useQuery(
        LIST_SUBSCRIPTION_PLANS,
        {
          currentSubscription: true,
        },
        {
          fetchPolicy: "network-only",
        }
      );

      onQuerySubscriptionPlansResult((result) => {
        if (result?.data) {
          console.log(result.data.listSubscriptionPlans);
          currentSubscription.value =
            result.data.listSubscriptionPlans?.currentSubscription || null;
          if (result.data?.listSubscriptionPlans?.allPlans?.length > 0) {
            handlePaymentMethod(
              result.data.listSubscriptionPlans?.activePaymentMethod
            );
            handleSubscriptionPlans(result.data.listSubscriptionPlans.allPlans);
            const current_subscription_plan_tier_id =
              result.data.listSubscriptionPlans?.currentSubscription
                ?.subscriptionPlanTierId;
            currentSubscriptionStatus.value =
              result.data.listSubscriptionPlans?.currentSubscription?.status;
            if (current_subscription_plan_tier_id) {
              currentActiveSubscriptionPlanTierId.value =
                current_subscription_plan_tier_id;
              currentSubscriptionAmount.value =
                result.data.listSubscriptionPlans?.currentSubscription?.amount;
              selectPlan(current_subscription_plan_tier_id);
            }
          } else {
            errorMessage.value = "No subscription plans found";
          }
          isFetchingSubscriptionPlans.value = false;
        }
      });

      onQuerySubscriptionPlansError((error) => {
        if (error) {
          errorMessage.value = "Failed to fetch subscription plans";
          isFetchingSubscriptionPlans.value = false;
        }
      });
    };

    const handleSubscriptionPlans = (subscriptionPlans: SubscriptionPlan[]) => {
      monthlyPlansList.value = [];
      yearlyPlansList.value = [];
      subscriptionPlans.forEach((plan) => {
        plan.subscriptionPlanTiers.forEach((tier) => {
          if (tier.billingCycle === "monthly") {
            pushToMonthlyPlansList(plan, tier);
          } else if (tier.billingCycle === "yearly") {
            pushToYearlyPlansList(plan, tier);
          }
        });
      });
    };

    const pushToMonthlyPlansList = (
      plan: SubscriptionPlan,
      tier: SubscriptionPlanTier
    ) => {
      monthlyPlansList.value.push({
        subscriptionPlanTierId: tier.id,
        name: plan.name,
        amount: tier.amount,
        billingCycle: tier.billingCycle,
        payoutFeePercentage: plan.payoutFeePercentage,
        features: plan.features,
      });
    };

    const pushToYearlyPlansList = (
      plan: SubscriptionPlan,
      tier: SubscriptionPlanTier
    ) => {
      yearlyPlansList.value.push({
        subscriptionPlanTierId: tier.id,
        name: plan.name,
        amount: tier.amount,
        billingCycle: tier.billingCycle,
        payoutFeePercentage: plan.payoutFeePercentage,
        features: plan.features,
      });
    };

    const handlePaymentMethod = (paymentMethod: PaymentMethod) => {
      paymentMethodSource.value = paymentMethod?.source || "";
      paymentMethodCardNumber.value =
        paymentMethod?.paymentInfo?.cardNumber || null;
      paymentMethodCardExpiry.value =
        paymentMethod?.paymentInfo?.expirationDate || null;
      paymentMethodInvoiceRecipientEmail.value =
        paymentMethod?.invoiceRecipientEmail || null;
    };

    const createSelectedSubscription = (subscriptionPlanTierId: string) => {
      errorMessage.value = "";
      if (
        currentActiveSubscriptionPlanTierId.value === subscriptionPlanTierId
      ) {
        errorMessage.value = "You are already on this plan";
        scrollToTop();
        return;
      }

      upsertSubscription({
        input: {
          subscriptionPlanTierId: subscriptionPlanTierId,
        },
      });

      upsertSubscriptionDone((response) => {
        if (response) {
          if (response.errors) {
            errorMessage.value = response.errors[0].message;
            scrollToTop();
          } else {
            if (response.data?.upsertSubscription?.success) {
              fetchSubscriptionPlans();
            } else {
              errorMessage.value =
                response.data?.upsertSubscription?.errors.join(", ");
              scrollToTop();
            }
          }
        }
      });

      upsertSubscriptionError((error) => {
        if (error) {
          errorMessage.value = "Failed to upsert subscription";
          scrollToTop();
        }
      });
    };

    const selectPlan = (subscriptionPlanTierId: string) => {
      let planFound = false;
      monthlyPlansList.value.forEach((plan) => {
        if (plan.subscriptionPlanTierId === subscriptionPlanTierId) {
          selectedPlan.value = plan;
          planFound = true;
        }
      });
      if (!planFound) {
        yearlyPlansList.value.forEach((plan) => {
          if (plan.subscriptionPlanTierId === subscriptionPlanTierId) {
            selectedPlan.value = plan;
            showYearlyPlans();
          }
        });
      }
    };

    const reviewPaymentMethod = (subscriptionPlanTierId: string) => {
      proceedWithSubscriptionPlanTierId.value = subscriptionPlanTierId;
      isModalLoading.value = true;
      isPaymentMethodModalVisible.value = true;
      planToReview.value = null;
      nextTick(() => {
        planToReview.value = subscriptionPlanTierId;
      });
    };

    const proceedWithPaymentMethod = () => {
      isPaymentMethodModalVisible.value = false;
      createSelectedSubscription(proceedWithSubscriptionPlanTierId.value);
    };

    const checkErrorMessage = () => {
      if (errorMessage.value) {
        errorMessage.value = "";
      }
    };

    const isLoading = computed(() => {
      return (
        upsertSubscriptionLoading.value || isFetchingSubscriptionPlans.value
      );
    });

    const handleContactSupport = () => {
      window.location.href = "mailto:help@slashexperts.com";
    };

    const getCurrentMonthlyAmount = () => {
      if (!selectedPlan.value) return 0;
      return selectedPlan.value.billingCycle === "yearly"
        ? selectedPlan.value.amount / 12
        : selectedPlan.value.amount;
    };

    return {
      isMonthlyPlansSelected,
      isYearlyPlansSelected,
      showMonthlyPlans,
      showYearlyPlans,
      monthlyPlansList,
      yearlyPlansList,
      selectedPlan,
      selectPlan,
      isLoading,
      errorMessage,
      createSelectedSubscription,
      currentSubscriptionStatus,
      currentSubscriptionAmount,
      isPaymentMethodModalVisible,
      paymentMethodSource,
      paymentMethodCardNumber,
      paymentMethodCardExpiry,
      paymentMethodInvoiceRecipientEmail,
      mappedUpcomingInvoice,
      currentSubscription,
      proceedWithPaymentMethod,
      reviewPaymentMethod,
      handleContactSupport,
      getCurrentMonthlyAmount,
      isModalLoading,
    };
  },
});
</script>
